import { useCallback, useEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils'

/**
 * Wide tables/lists with a horizontal scrollbar at the top (synced with the main area).
 */
export function HorizontalScrollArea({
  children,
  className,
  mainClassName,
}) {
  const topRef = useRef(null)
  const mainRef = useRef(null)
  const contentRef = useRef(null)
  const [spacerWidth, setSpacerWidth] = useState(0)
  const [showTopBar, setShowTopBar] = useState(false)

  const syncSizes = useCallback(() => {
    const content = contentRef.current
    const main = mainRef.current
    if (!content || !main) return
    setSpacerWidth(content.scrollWidth)
    setShowTopBar(content.scrollWidth > main.clientWidth + 1)
  }, [])

  useEffect(() => {
    syncSizes()
    const content = contentRef.current
    if (!content) return undefined
    const observer = new ResizeObserver(syncSizes)
    observer.observe(content)
    window.addEventListener('resize', syncSizes)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', syncSizes)
    }
  }, [syncSizes, children])

  useEffect(() => {
    const top = topRef.current
    const main = mainRef.current
    if (!top || !main) return undefined

    let syncing = false
    const onMainScroll = () => {
      if (syncing) return
      syncing = true
      top.scrollLeft = main.scrollLeft
      syncing = false
    }
    const onTopScroll = () => {
      if (syncing) return
      syncing = true
      main.scrollLeft = top.scrollLeft
      syncing = false
    }

    main.addEventListener('scroll', onMainScroll)
    top.addEventListener('scroll', onTopScroll)
    return () => {
      main.removeEventListener('scroll', onMainScroll)
      top.removeEventListener('scroll', onTopScroll)
    }
  }, [showTopBar])

  return (
    <div className={cn('rounded-lg border border-border', className)}>
      {showTopBar && (
        <div
          ref={topRef}
          className="horizontal-scroll-top overflow-x-auto overflow-y-hidden border-b border-border/60"
          aria-hidden
        >
          <div style={{ width: spacerWidth, height: 1 }} />
        </div>
      )}
      <div ref={mainRef} className={cn('overflow-x-auto overflow-y-hidden', mainClassName)}>
        <div ref={contentRef}>{children}</div>
      </div>
    </div>
  )
}
